import { trace } from "owa-trace";

/** Enum representing smart schedule generation job status (mirrors service-side JobStatus) */
export enum JobStatus {
    Failure = "Failure",
    Success = "Success",
    Queued = "Queued",
    InProgress = "InProgress",
    PartialSuccess = "PartialSuccess",
    Canceled = "Canceled"
}

/** Interface for the smart schedule generation job status result sub-object */
export interface ISmartScheduleGenerationJobErrorEntity {
    code: string;
    message: string;
}

export interface ISmartScheduleGenerationJobResultEntity {
    teamId?: string;
    tagId?: string;
    jobId?: string;
    /** Identifier for the member associated with the generation */
    memberId?: string;
    /** Shift IDs created by the smart schedule process */
    createdShiftIds?: string[] | null; // nullable per service example
    /** Open shift IDs created by the smart schedule process */
    createdOpenShiftIds?: string[] | null;
    /** Error details object if generation failed, null on success */
    error?: ISmartScheduleGenerationJobErrorEntity | null;
}

/** Interface for job properties controlling / describing generation behavior */
export interface ISmartScheduleGenerationJobPropertiesEntity {
    overrideExistingSchedule: boolean;
    isReviewed: boolean;
    startDate?: string; // ISO date
    endDate?: string; // ISO date
}

/** Interface for smart schedule generation job status top-level entity */
export interface ISmartScheduleGenerationJobStatusEntity {
    jobId: string;
    tagId?: string;
    memberId?: string;
    teamId?: string;
    /** UTC ISO timestamp when job started */
    jobStartedTime?: string;
    /** UTC ISO timestamp when job completed (terminal status) */
    jobCompletedTime?: string;
    status: JobStatus;
    result?: ISmartScheduleGenerationJobResultEntity;
    jobProperties?: ISmartScheduleGenerationJobPropertiesEntity;
}

/** Options for polling smart schedule generation job status */
export interface PollSmartScheduleGenerationJobStatusOptions {
    intervalMs?: number; // polling interval
    timeoutMs?: number; // overall timeout duration
    terminalStatuses?: string[]; // statuses that terminate polling
    onTick?: (status: ISmartScheduleGenerationJobStatusEntity, attempt: number) => void; // optional callback invoked each poll
}

/** Client model parser for smart schedule generation job status */
export class SmartScheduleGenerationJobStatusEntity implements ISmartScheduleGenerationJobStatusEntity {
    jobId: string;
    tagId?: string;
    memberId?: string;
    teamId?: string;
    jobStartedTime?: string;
    jobCompletedTime?: string;
    status: JobStatus;
    result?: ISmartScheduleGenerationJobResultEntity;
    jobProperties?: ISmartScheduleGenerationJobPropertiesEntity;

    constructor(params: ISmartScheduleGenerationJobStatusEntity) {
        this.jobId = params.jobId;
        this.tagId = params.tagId;
        this.memberId = params.memberId;
        this.teamId = params.teamId;
        this.jobStartedTime = params.jobStartedTime;
        this.jobCompletedTime = params.jobCompletedTime;
        this.status = params.status;
        this.result = params.result;
        this.jobProperties = params.jobProperties;
    }

    static fromJson(json: any): ISmartScheduleGenerationJobStatusEntity {
        if (!json) {
            trace.warn("SmartScheduleGenerationJobStatusEntity.fromJson called with empty json");
            return null;
        }
        return new SmartScheduleGenerationJobStatusEntity({
            jobId: json.jobId,
            tagId: json.tagId,
            memberId: json.memberId,
            teamId: json.teamId,
            status: json.status,
            jobStartedTime: json.jobStartedTime,
            jobCompletedTime: json.jobCompletedTime,
            result: json.result
                ? {
                      teamId: json.teamId,
                      tagId: json.tagId,
                      jobId: json.jobId,
                      memberId: json.memberId,
                      createdShiftIds: Array.isArray(json.result.createdShiftIds)
                          ? json.result.createdShiftIds
                          : (json.result.createdShiftIds === null ? null : []),
                      createdOpenShiftIds: Array.isArray(json.result.createdOpenShiftIds)
                          ? json.result.createdOpenShiftIds
                          : (json.result.createdOpenShiftIds === null ? null : []),
                      error: json.result.error && typeof json.result.error === 'object'
                          ? {
                                code: json.result.error.code ?? 'Unknown',
                                message: json.result.error.message ?? ''
                            }
                          : null
                  }
                : undefined,
            jobProperties: json.jobProperties
                ? {
                      overrideExistingSchedule: !!json.jobProperties.overrideExistingSchedule,
                      isReviewed: !!json.jobProperties.isReviewed,
                      startDate: json.jobProperties.startDate,
                      endDate: json.jobProperties.endDate
                  }
                : undefined
        });
    }
}
